import React, { useEffect, useState } from 'react';
import qs from 'querystring';
import { useLocation, useHistory } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Spin from 'components/common/Spin';
import Button from 'components/common/Button';
import useSnack from 'hooks/useSnack';
import { Query, useApolloClient, useMutation } from 'react-apollo';
import {
  GET_ACCOUNT,
  ADD_CALENDAR,
  DELETE_CALENDAR,
  UPDATE_CALENDAR_ACCOUNT,
} from 'graphql/calendar';
import { DEFAULT_ZONE } from 'constant/timezones';
import Connect from './Connect';
import CronofyUI from './CronofyUI';
import EmailAddressModal from './EmailAddressModal';
import Settings from './Settings';
import moment from 'moment-timezone';
import SlotPicker from 'components/pages/calendar/SlotPicker';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';


const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  paperFullWidth: {
    maxWidth: 700,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h5">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    'padding': theme.spacing(3),
    'paddingTop': 0,
    '& .MuiPickersStaticWrapper-staticWrapperRoot': {
      padding: theme.spacing(1),
      borderRadius: 6,
      backgroundColor: 'rgba(248, 249, 252, 0.7)',
    },
    '& .MuiPickersCalendar-transitionContainer': {
      minHeight: 190,
    },
  },
}))(MuiDialogContent);

const Calendar = ({ user, classes }) => {
  const [saving, setSaving] = useState(false);
  const [showSlots, setShowSlots] = useState(false)
  const client = useApolloClient();
  const [deleteCalendar] = useMutation(DELETE_CALENDAR);
  const [updateCalendar] = useMutation(UPDATE_CALENDAR_ACCOUNT);
  const location = useLocation();
  const history = useHistory();
  const snack = useSnack();
  const timezone = user.timezone || DEFAULT_ZONE;
  const availablePeriods = {
    start: moment
      .utc()
      .add(1, 'd')
      .format(),
    end: moment
      .utc()
      .add(30, 'd')
      .format(),
  };

  useEffect(() => {
    const query = qs.parse(location.search.substr(1));
    if (query.code) {
      setSaving(true);
      client
        .mutate({
          mutation: ADD_CALENDAR,
          variables: {
            code: query.code,
          },
        })
        .catch((e) => snack.error(e.message))
        .finally(() => {
          setSaving(false);
          history.push('/calendar');
        });
    }
  }, []);

  const onDisconnect = async (id) => {
    try {
      if (window.confirm('Are you sure?')) {
        setSaving(true);
        await deleteCalendar({
          variables: { id },
        });
        snack.success('Calendar disconnected.');
      }
    } catch (e) {
      snack.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (saving) {
    return <Spin spinning linear />;
  }

  return (
    <Query query={GET_ACCOUNT} variables={{
      userId: user.id,
    }} fetchPolicy={'cache-and-network'}>
      {({ data, loading, error, refetch }) => {
        if (error)
          return (
            <Alert variant={'filled'} color={'error'}>
              {error.message}
            </Alert>
          );
        if (loading) return <Spin spinning linear />;

        const { accounts } = data;
        const account = accounts[0];
        if (!account || !account.active) {
          return <Connect />;
        }

        const onSaveEmail = (email) =>
          updateCalendar({
            variables: {
              id: account.id,
              data: {
                email,
              },
            },
          }).then(
            () => {
              snack.success('Account updated.');
            },
            (e) => snack.error(e.message),
          );

        if (
          account.provider === 'apple' &&
          !(
            account.email.endsWith('@icloud.com') ||
            account.email.endsWith('@me.com')
          )
        ) {
          return <EmailAddressModal onSubmit={onSaveEmail} />;
        }

        return (
          <div>
            <CronofyUI timezone={timezone} />
            <Settings account={account} />
           <Box
              display="flex"
              justifyContent="space-between"
              textAlign="right"
              marginTop={3}
            >
              <Button
                onClick={() => setShowSlots(true)}
                variant="outlined"
                color="primary"
              >
                View Available Slots
              </Button>
              <Button
                onClick={() => onDisconnect(account.id)}
                variant="outlined"
                color="secondary"
              >
                Disconnect Calendar
              </Button>
            </Box>

            {showSlots && (
              <Dialog
                onClose={() => setShowSlots(false)}
                fullWidth
                maxWidth={false}
                classes={{ paperFullWidth: classes.paperFullWidth }}
                open
              >
                <DialogTitle onClose={() => setShowSlots(false)}>{`Available Slots`}</DialogTitle>
                <DialogContent>
                  <SlotPicker
                    userIds={[user.id]}
                    availablePeriods={availablePeriods}
                    duration={user.type === 'lead_coach' ? 45 : 30}
                    onPick={() => {}}
                    timezone={timezone}
                  />
                </DialogContent>
              </Dialog>
            )}

          </div>
        );
      }}
    </Query>
  );
};

export default withStyles(styles)(Calendar);;
 
